import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import type {
  AgentsListRequest,
  AgentsListResponse,
  AgentsSortBy,
  AgentsSortOrder,
} from '@/pages/analytics/types';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';

import { analyticsQueryKeys } from './useOrgAnalytics';

// Drop agents missing an id or name — backend placeholder rows we don't want
// surfaced in the table.
const dropUnnamedAgents = (res: AgentsListResponse): AgentsListResponse => ({
  ...res,
  agents: res.agents.filter(a => !!a.agent_id && !!a.agent_name),
});

interface UseAgentsListArgs {
  organizationId: string | null;
  programIds?: string[];
  agentIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
  scoreGte?: number;
  scoreLte?: number;
  sortBy?: AgentsSortBy;
  sortOrder?: AgentsSortOrder;
  page?: number;
  pageSize?: number;
}

const joinOrUndef = (ids?: string[]): string | undefined =>
  ids && ids.length ? ids.join(',') : undefined;

const buildAgentsListParams = (args: UseAgentsListArgs): AgentsListRequest => ({
  organization_id: args.organizationId ?? '',
  program_ids: joinOrUndef(args.programIds),
  agent_ids: joinOrUndef(args.agentIds),
  call_directions: joinOrUndef(args.callDirections),
  start: args.start ? naiveDateToUtc(args.start, args.timeZone, 'start') : undefined,
  end: args.end ? naiveDateToUtc(args.end, args.timeZone, 'end') : undefined,
  score_gte: args.scoreGte,
  score_lte: args.scoreLte,
  sort_by: args.sortBy ?? 'avg_score',
  sort_order: args.sortOrder ?? 'desc',
  page: args.page ?? 1,
  page_size: args.pageSize ?? 20,
});

export const useAgentsList = (args: UseAgentsListArgs) => {
  const params = buildAgentsListParams(args);
  const { organization_id, ...queryParams } = params;

  return useQuery({
    queryKey: [...analyticsQueryKeys.all, 'agents', 'list', params] as const,
    queryFn: async () =>
      (await analyticsService.getAgentsList(
        { organization_id },
        { params: queryParams },
      )) as AgentsListResponse,
    select: dropUnnamedAgents,
    enabled: !!args.organizationId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
